import { createContext, useContext, useEffect, useRef, type PropsWithChildren, type FC, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

type LocationHistoryContextValue = {
  getCurrentPath: () => string | null
  getPreviousPath: () => string | null
}

const LocationHistoryContext = createContext<LocationHistoryContextValue | null>(null)

export function useLocationHistory(): LocationHistoryContextValue {
  const context = useContext(LocationHistoryContext)

  if (!context) {
    throw new Error('useLocationHistory must be used within a LocationHistoryProvider')
  }

  return context
}

export const LocationHistoryProvider: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation()
  const previousPathRef = useRef<string | null>(null)
  const currentPathRef = useRef<string>(location.pathname)

  useEffect(() => {
    if (location.pathname !== currentPathRef.current) {
      previousPathRef.current = currentPathRef.current
      currentPathRef.current = location.pathname
    }
  }, [location.pathname])

  const value: LocationHistoryContextValue = useMemo(
    () => ({
      getCurrentPath: () => currentPathRef.current,
      getPreviousPath: () => previousPathRef.current,
    }),
    []
  )

  return <LocationHistoryContext.Provider value={value}>{children}</LocationHistoryContext.Provider>
}
